/**
 * Turn an error from allauth into something a form can show.
 *
 * Field errors are set on the form, so "that email is taken" appears under the email box exactly
 * as a client-side error does. Anything without a field becomes a message for the whole form.
 * A pending flow is not a failure at all: allauth is naming the next step, such as
 * `verify_email` after a sign-up, and the page branches on it.
 */

import type { FieldValues, Path, UseFormSetError } from "react-hook-form";
import { AuthError } from "@/api/auth";

export interface AuthFailure {
  /** A message about the whole form, already in the user's language. */
  formError?: string;
  /** The step allauth is waiting on, if any. */
  pendingFlow?: string;
}

export function applyAuthError<TValues extends FieldValues>(
  error: unknown,
  options: {
    setError: UseFormSetError<TValues>;
    /** Fields allauth may name, mapped back onto the form. */
    fields: readonly Path<TValues>[];
    /** Message for anything we cannot attribute, already translated. */
    fallback: string;
  },
): AuthFailure {
  if (!(error instanceof AuthError)) return { formError: options.fallback };
  if (error.pendingFlow) return { pendingFlow: error.pendingFlow };

  let handled = false;
  for (const field of options.fields) {
    const message = error.errorFor(field as string);
    if (message) {
      // `type: "server"` marks it as already-English text, so the page does not translate it.
      options.setError(field, { type: "server", message });
      handled = true;
    }
  }

  const rest = error.formErrors;
  if (rest.length > 0) return { formError: rest.join(" ") };
  if (!handled) return { formError: error.message || options.fallback };
  return {};
}
